import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin, theme as antTheme } from 'antd';
import { useThemeStore } from './store/theme-store';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import i18n from './i18n';
import { LoginPage } from './features/auth/routes/LoginPage';
import { RegisterPage } from './features/auth/routes/RegisterPage';
import { ProtectedRoute } from './features/auth/routes/ProtectedRoute';
import { AppLayout } from './components/common/AppLayout';
import { SubscriptionGate } from './features/subscription/components/SubscriptionGate';

// Route-level code splitting — each page loads on demand
const DashboardPage = lazy(() => import('./features/dashboard/routes/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ProductListPage = lazy(() => import('./features/products/routes/ProductListPage').then(m => ({ default: m.ProductListPage })));
const ProductCreatePage = lazy(() => import('./features/products/routes/ProductCreatePage').then(m => ({ default: m.ProductCreatePage })));
const ProductEditPage = lazy(() => import('./features/products/routes/ProductEditPage').then(m => ({ default: m.ProductEditPage })));
const OrderEntryPage = lazy(() => import('./features/orders/routes/OrderEntryPage'));
const OrderListPage = lazy(() => import('./features/orders/routes/OrderListPage').then(m => ({ default: m.OrderListPage })));
const OrderCreatePage = lazy(() => import('./features/orders/routes/OrderCreatePage').then(m => ({ default: m.OrderCreatePage })));
const OrderDetailPage = lazy(() => import('./features/orders/routes/OrderDetailPage').then(m => ({ default: m.OrderDetailPage })));
const QuickOrderPage = lazy(() => import('./features/orders/routes/QuickOrderPage').then(m => ({ default: m.QuickOrderPage })));
const InventoryListPage = lazy(() => import('./features/inventory/routes/InventoryListPage').then(m => ({ default: m.InventoryListPage })));
const InventoryOperationsPage = lazy(() => import('./features/inventory/routes/InventoryScanPage').then(m => ({ default: m.InventoryOperationsPage })));
const OutboundRecordsPage = lazy(() => import('./features/inventory/routes/OutboundRecordsPage').then(m => ({ default: m.OutboundRecordsPage })));
const InboundRecordsPage = lazy(() => import('./features/inventory/routes/InboundRecordsPage').then(m => ({ default: m.InboundRecordsPage })));
const ChannelManagementPage = lazy(() => import('./features/channels/routes/ChannelManagementPage').then(m => ({ default: m.ChannelManagementPage })));
const PurchaseOrderListPage = lazy(() => import('./features/procurement/routes/PurchaseOrderListPage').then(m => ({ default: m.PurchaseOrderListPage })));
const PurchaseOrderCreatePage = lazy(() => import('./features/procurement/routes/PurchaseOrderCreatePage').then(m => ({ default: m.PurchaseOrderCreatePage })));
const PurchaseOrderDetailPage = lazy(() => import('./features/procurement/routes/PurchaseOrderDetailPage').then(m => ({ default: m.PurchaseOrderDetailPage })));
const PurchaseOrderPrintPage = lazy(() => import('./features/procurement/routes/PurchaseOrderPrintPage').then(m => ({ default: m.PurchaseOrderPrintPage })));
const PurchaseRequestListPage = lazy(() => import('./features/purchase-requests/routes/PurchaseRequestListPage').then(m => ({ default: m.PurchaseRequestListPage })));
const PurchaseRequestCreatePage = lazy(() => import('./features/purchase-requests/routes/PurchaseRequestCreatePage').then(m => ({ default: m.PurchaseRequestCreatePage })));
const PurchaseRequestAggregatedPage = lazy(() => import('./features/purchase-requests/routes/PurchaseRequestAggregatedPage').then(m => ({ default: m.PurchaseRequestAggregatedPage })));
const PickListPage = lazy(() => import('./features/pick-lists/routes/PickListPage').then(m => ({ default: m.PickListPage })));
const PickListDetailPage = lazy(() => import('./features/pick-lists/routes/PickListDetailPage').then(m => ({ default: m.PickListDetailPage })));
const FlightBatchPage = lazy(() => import('./features/flight-batches/routes/FlightBatchPage').then(m => ({ default: m.FlightBatchPage })));
const FlightBatchCreatePage = lazy(() => import('./features/flight-batches/routes/FlightBatchCreatePage').then(m => ({ default: m.FlightBatchCreatePage })));
const FlightBatchDetailPage = lazy(() => import('./features/flight-batches/routes/FlightBatchDetailPage').then(m => ({ default: m.FlightBatchDetailPage })));
const CustomerListPage = lazy(() => import('./features/customers/routes/CustomerListPage').then(m => ({ default: m.CustomerListPage })));
const SupplierListPage = lazy(() => import('./features/suppliers/routes/SupplierListPage').then(m => ({ default: m.SupplierListPage })));
const WarehouseListPage = lazy(() => import('./features/warehouses/routes/WarehouseListPage').then(m => ({ default: m.WarehouseListPage })));
const UserListPage = lazy(() => import('./features/users/routes/UserListPage').then(m => ({ default: m.UserListPage })));
const RoleManagementPage = lazy(() => import('./features/users/routes/RoleManagementPage').then(m => ({ default: m.RoleManagementPage })));
const AuditLogPage = lazy(() => import('./features/audit-logs/routes/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const LogisticsCompanyListPage = lazy(() => import('./features/logistics-companies/routes/LogisticsCompanyListPage').then(m => ({ default: m.LogisticsCompanyListPage })));
const OrderTrackingPage = lazy(() => import('./features/orders/routes/OrderTrackingPage').then(m => ({ default: m.OrderTrackingPage })));
const TaobaoOrdersPage = lazy(() => import('./features/taobao/routes/TaobaoOrdersPage').then(m => ({ default: m.TaobaoOrdersPage })));
const TaobaoSettingsPage = lazy(() => import('./features/taobao/routes/TaobaoSettingsPage').then(m => ({ default: m.TaobaoSettingsPage })));
const TenantListPage = lazy(() => import('./features/tenants/routes/TenantListPage').then(m => ({ default: m.TenantListPage })));
const SubscriptionPage = lazy(() => import('./features/subscription/routes/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const TenantSettingsPage = lazy(() => import('./features/tenant-settings/routes/TenantSettingsPage').then(m => ({ default: m.TenantSettingsPage })));
const StockTakeListPage = lazy(() => import('./features/stock-takes/routes/StockTakeListPage').then(m => ({ default: m.StockTakeListPage })));
const StockTakeScanPage = lazy(() => import('./features/stock-takes/routes/StockTakeScanPage').then(m => ({ default: m.StockTakeScanPage })));
const StockTakeReviewPage = lazy(() => import('./features/stock-takes/routes/StockTakeReviewPage').then(m => ({ default: m.StockTakeReviewPage })));

// Initialize Day.js locale
dayjs.locale('zh-cn');

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <Spin size="large" />
  </div>
);

function App() {
  const darkMode = useThemeStore((s) => s.darkMode);
  const [locale, setLocale] = useState(i18n.language === 'en' ? enUS : zhCN);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLocale(lng === 'en' ? enUS : zhCN);
      dayjs.locale(lng === 'en' ? 'en' : 'zh-cn');
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        algorithm: darkMode ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          fontFamily: "'Inter', sans-serif",
          colorPrimary: '#1677ff',
          borderRadius: 8,
          ...(darkMode ? {} : { colorBgContainer: '#ffffff' }),
        },
        components: {
          Button: {
            controlHeight: 40,
            borderRadius: 8,
            fontWeight: 500,
          },
          Card: {
            borderRadiusLG: 12,
          },
          Input: {
            controlHeight: 40,
          },
          Select: {
            controlHeight: 40,
          }
        }
      }}
    >
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/track" element={<OrderTrackingPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<DashboardPage />} />

              <Route path="/products" element={<ProductListPage />} />
              <Route path="/products/new" element={<ProductCreatePage />} />
              <Route path="/products/edit/:id" element={<ProductEditPage />} />

              <Route path="/orders" element={<OrderListPage />} />
              <Route path="/orders/new" element={<OrderCreatePage />} />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              <Route path="/orders/quick" element={<QuickOrderPage />} />
              <Route path="/orders/entry" element={<OrderEntryPage />} />

              <Route path="/inventory" element={<InventoryListPage />} />
              <Route path="/inventory/scan" element={<InventoryOperationsPage />} />
              <Route path="/inventory/outbound-records" element={<OutboundRecordsPage />} />
              <Route path="/inventory/inbound-records" element={<InboundRecordsPage />} />
              <Route path="/stock-takes" element={<StockTakeListPage />} />
              <Route path="/stock-takes/:id/scan" element={<StockTakeScanPage />} />
              <Route path="/stock-takes/:id/review" element={<StockTakeReviewPage />} />
              <Route path="/channels" element={<ChannelManagementPage />} />

              <Route path="/procurement/purchase-orders" element={<PurchaseOrderListPage />} />
              <Route path="/procurement/purchase-orders/new" element={<PurchaseOrderCreatePage />} />
              <Route path="/procurement/purchase-orders/:id/edit" element={<PurchaseOrderCreatePage />} />
              <Route path="/procurement/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />

              <Route path="/purchase-requests" element={<PurchaseRequestListPage />} />
              <Route path="/purchase-requests/new" element={<PurchaseRequestCreatePage />} />
              <Route path="/purchase-requests/aggregated" element={<PurchaseRequestAggregatedPage />} />

              <Route path="/pick-lists" element={<PickListPage />} />
              <Route path="/pick-lists/:id" element={<PickListDetailPage />} />
              {/* Flight batches require Pro tier — backend returns 402 anyway, but
                  the gate renders the upgrade prompt instead of triggering the
                  toast-on-error fallback. */}
              <Route path="/flight-batches" element={<SubscriptionGate requiredTier="PRO"><FlightBatchPage /></SubscriptionGate>} />
              <Route path="/flight-batches/new" element={<SubscriptionGate requiredTier="PRO"><FlightBatchCreatePage /></SubscriptionGate>} />
              <Route path="/flight-batches/:id" element={<SubscriptionGate requiredTier="PRO"><FlightBatchDetailPage /></SubscriptionGate>} />

              <Route path="/customers" element={<CustomerListPage />} />
              <Route path="/suppliers" element={<SupplierListPage />} />
              <Route path="/warehouses" element={<WarehouseListPage />} />
              <Route path="/users" element={<UserListPage />} />
              <Route path="/roles" element={<RoleManagementPage />} />
              <Route path="/logistics-companies" element={<LogisticsCompanyListPage />} />
              {/* Taobao integration is Pro+. */}
              <Route path="/taobao/orders" element={<SubscriptionGate requiredTier="PRO"><TaobaoOrdersPage /></SubscriptionGate>} />
              <Route path="/taobao/settings" element={<SubscriptionGate requiredTier="PRO"><TaobaoSettingsPage /></SubscriptionGate>} />

              <Route path="/audit-logs" element={<AuditLogPage />} />
              <Route path="/tenants" element={<TenantListPage />} />
              <Route path="/settings/subscription" element={<SubscriptionPage />} />
              <Route path="/settings/tenant" element={<TenantSettingsPage />} />

            </Route>

            {/* Print-only views render OUTSIDE AppLayout so there is no
                sidebar / top nav to hide for printing — what shows on
                screen is what prints. Still inside ProtectedRoute so
                auth applies. */}
            <Route
              path="/procurement/purchase-orders/:id/print"
              element={<PurchaseOrderPrintPage />}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ConfigProvider>
  );
}

export default App;
